import * as React from "react";
import { cx } from "../internal/cx.js";

export type BadgeVariant =
  | "brand"
  | "danger"
  | "neutral"
  | "on-ink"
  | "outline"
  | "success"
  | "warning";

export type BadgeSize = "xs" | "sm" | "md" | "lg";

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Convenience: if provided, the badge renders this string with a
   * capitalized first letter. For richer content, pass `children` instead
   * and skip `label`.
   */
  label?: string;
  /** Required when `removable`. Fires on click of the × affordance. */
  onRemove?: () => void;
  removable?: boolean;
  size?: BadgeSize;
  variant?: BadgeVariant;
}

function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Pill-shaped status label. Use for taxonomy chips, severity markers, or
 * removable filter tokens. Visual weight tracks `variant`; size only
 * affects padding + font scale (not the colour).
 */
export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(function Badge(
  {
    children,
    className,
    label,
    onRemove,
    removable = false,
    size = "sm",
    variant = "neutral",
    ...props
  },
  ref,
) {
  const content = label ? capitalize(label) : children;
  return (
    <div
      ref={ref}
      className={cx("cw-v2-badge", `cw-v2-badge--${variant}`, `cw-v2-badge--${size}`, className)}
      {...props}
    >
      <span>{content}</span>
      {removable ? (
        <button
          aria-label="Remove badge"
          className="cw-v2-badge__remove"
          onClick={onRemove}
          type="button"
        >
          <svg aria-hidden="true" viewBox="0 0 12 12">
            <title>Remove</title>
            <path
              d="M3 3L9 9M9 3L3 9"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeWidth="1.5"
            />
          </svg>
        </button>
      ) : null}
    </div>
  );
});

Badge.displayName = "Badge";
